import React, { useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { LoadingState, StatCard, DonutChart, fmt } from './ReportHelpers';

export function ItemsUsageTab({ data, loading, onExport }) {
  const [typeFilter, setTypeFilter] = useState('all');

  if (loading) return <LoadingState />;
  if (!data) return null;

  const { total_items, grand_quoted, grand_actual, grand_variance, by_type, cost_by_type, items } = data;

  const filtered = typeFilter === 'all' ? items : items.filter(i => i.type_label === typeFilter);

  const handleExport = () => {
    const headers = ['Item Name', 'Type', 'Quoted Qty', 'Actual Qty', 'Quoted Cost', 'Actual Cost', 'Variance', 'Jobs Used In'];
    const rows = filtered.map(r => [r.item_name, r.type_label, r.total_quoted_qty, r.total_actual_qty, r.total_quoted_cost, r.total_actual_cost, r.variance, r.job_count]);
    const summary = ['', '', '', '', fmt(grand_quoted), fmt(grand_actual), fmt(grand_variance), ''];
    onExport('Items Usage Report', headers, rows, summary);
  };

  const typeOptions = ['all', ...by_type.map(t => t.name)];

  return (
    <div className="space-y-5" data-testid="items-usage-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Unique Items" value={total_items} />
        <StatCard label="Total Quoted" value={fmt(grand_quoted)} />
        <StatCard label="Total Actual" value={fmt(grand_actual)} />
        <StatCard label="Overall Variance" value={fmt(grand_variance)} sub={grand_variance >= 0 ? 'Under budget' : 'Over budget'} color={grand_variance >= 0 ? 'text-green-600' : 'text-red-600'} />
      </div>

      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-slate-500">Filter by Type:</label>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded text-sm"
            data-testid="items-type-filter"
          >
            {typeOptions.map(t => (
              <option key={t} value={t}>{t === 'all' ? 'All Types' : t}</option>
            ))}
          </select>
        </div>
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700" data-testid="export-items-usage-excel">
          <FileSpreadsheet size={14} /> Export Excel
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <DonutChart data={by_type} title="Items by Type (Count)" />
        {cost_by_type.length > 0 && (
          <div className="bg-white rounded-[10px] border p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Cost by Type (Quoted vs Actual)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={cost_by_type}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="quoted" name="Quoted" fill="#f4931d" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#178c98" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white rounded-[10px] border overflow-x-auto">
        <table className="w-full text-sm" data-testid="items-usage-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Item Name</th>
              <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Type</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Quoted Qty</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Actual Qty</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Quoted Cost</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Actual Cost</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Variance</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-600 whitespace-nowrap">Jobs</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No items found for this period</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={`${r.item_id || r.item_name}-${r.type_label || ''}-${i}`} className="hover:bg-orange-50/30 transition-colors" data-testid={`item-row-${i}`}>
                <td className="px-4 py-2.5 font-medium">{r.item_name}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    r.type_label === 'Materials' ? 'bg-blue-100 text-blue-700' :
                    r.type_label === 'Labour' ? 'bg-purple-100 text-purple-700' :
                    r.type_label === 'MISC' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{r.type_label}</span>
                </td>
                <td className="px-4 py-2.5 text-right">{r.total_quoted_qty}</td>
                <td className="px-4 py-2.5 text-right">{r.total_actual_qty}</td>
                <td className="px-4 py-2.5 text-right">{fmt(r.total_quoted_cost)}</td>
                <td className="px-4 py-2.5 text-right">{fmt(r.total_actual_cost)}</td>
                <td className={`px-4 py-2.5 text-right font-medium ${r.variance > 0 ? 'text-green-600' : r.variance < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                  {fmt(r.variance)}
                </td>
                <td className="px-4 py-2.5 text-center">{r.job_count}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="bg-slate-800 text-white font-semibold">
                <td className="px-4 py-3">TOTAL</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-right">{fmt(filtered.reduce((s, r) => s + r.total_quoted_cost, 0))}</td>
                <td className="px-4 py-3 text-right">{fmt(filtered.reduce((s, r) => s + r.total_actual_cost, 0))}</td>
                <td className="px-4 py-3 text-right">{fmt(filtered.reduce((s, r) => s + r.variance, 0))}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
